/**
 * ConstraintConfigForm - Typed form for constraint configuration per type (C5)
 *
 * Renders type-specific inputs instead of raw JSON textarea.
 * Includes a JSON toggle for advanced editing.
 */
import React, { useState, useEffect } from "react";
import type { ConstraintType } from "../../../../types/adminBilling";

export interface ConstraintConfigFormProps {
  constraintType: ConstraintType;
  config: Record<string, unknown>;
  onChange: (config: Record<string, unknown>) => void;
  error?: string | null;
}

export const ConstraintConfigForm: React.FC<ConstraintConfigFormProps> = ({
  constraintType,
  config,
  onChange,
  error,
}) => {
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState(JSON.stringify(config, null, 2));
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Sync jsonText when config changes from typed form
  useEffect(() => {
    if (!jsonMode) {
      setJsonText(JSON.stringify(config, null, 2));
    }
  }, [config, jsonMode]);

  const handleJsonChange = (text: string) => {
    setJsonText(text);
    try {
      const parsed = JSON.parse(text);
      setJsonError(null);
      onChange(parsed);
    } catch {
      setJsonError("Nieprawidłowy JSON");
    }
  };

  const handleSwitchToJson = () => {
    setJsonText(JSON.stringify(config, null, 2));
    setJsonError(null);
    setJsonMode(true);
  };

  const handleSwitchToTyped = () => {
    // Try to parse current JSON to sync back
    try {
      const parsed = JSON.parse(jsonText);
      onChange(parsed);
    } catch {
      // Keep current config if JSON is invalid
    }
    setJsonMode(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="block text-xs font-medium text-neutral-700">
          Konfiguracja ograniczenia
        </label>
        <label className="flex items-center gap-1.5 text-xs text-neutral-500 cursor-pointer">
          <input
            type="checkbox"
            checked={jsonMode}
            onChange={() => (jsonMode ? handleSwitchToTyped() : handleSwitchToJson())}
            className="h-3 w-3 text-primary-600 border-neutral-300 rounded"
          />
          Edycja JSON
        </label>
      </div>

      {jsonMode ? (
        <div>
          <textarea
            value={jsonText}
            onChange={(e) => handleJsonChange(e.target.value)}
            rows={3}
            className={`w-full border rounded-md px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              jsonError ? "border-red-300" : "border-neutral-300"
            }`}
            placeholder="{}"
          />
          {jsonError && (
            <p className="text-red-600 text-xs mt-1">{jsonError}</p>
          )}
        </div>
      ) : (
        <TypedConfigForm
          constraintType={constraintType}
          config={config}
          onChange={onChange}
        />
      )}
      {error && !jsonMode && (
        <p className="text-red-600 text-xs mt-1">{error}</p>
      )}
    </div>
  );
};

// Extracted REGEX sub-component to comply with Rules of Hooks
const RegexConfigForm: React.FC<{
  config: Record<string, unknown>;
  onChange: (config: Record<string, unknown>) => void;
}> = ({ config, onChange }) => {
  const pattern = typeof config.pattern === "string" ? config.pattern : "";
  const [testValue, setTestValue] = useState("");
  let matchResult: boolean | null = null;
  if (pattern && testValue) {
    try {
      matchResult = new RegExp(pattern).test(testValue);
    } catch {
      matchResult = null;
    }
  }
  let patternValid = true;
  if (pattern) {
    try {
      new RegExp(pattern);
    } catch {
      patternValid = false;
    }
  }
  return (
    <div className="space-y-2">
      <div>
        <label className="block text-xs text-neutral-500 mb-0.5">Wzorzec regex</label>
        <input
          type="text"
          value={pattern}
          onChange={(e) => onChange({ ...config, pattern: e.target.value })}
          placeholder="np. ^[A-Z]{2}-\\d{4}$"
          className={`w-full border rounded-md px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            pattern && !patternValid ? "border-red-300" : "border-neutral-300"
          }`}
        />
        {pattern && !patternValid && (
          <p className="text-red-600 text-xs mt-0.5">Nieprawidłowy wzorzec regex</p>
        )}
      </div>
      {pattern && patternValid && (
        <div>
          <label className="block text-xs text-neutral-500 mb-0.5">Test wzorca</label>
          <div className="flex gap-2 items-center">
            <input
              type="text"
              value={testValue}
              onChange={(e) => setTestValue(e.target.value)}
              placeholder="Wpisz tekst do przetestowania..."
              className="flex-1 border border-neutral-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {testValue && matchResult !== null && (
              <span
                className={`text-xs font-medium px-2 py-1 rounded ${
                  matchResult
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {matchResult ? "Pasuje" : "Nie pasuje"}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Internal component for type-specific forms
const TypedConfigForm: React.FC<{
  constraintType: ConstraintType;
  config: Record<string, unknown>;
  onChange: (config: Record<string, unknown>) => void;
}> = ({ constraintType, config, onChange }) => {
  switch (constraintType) {
    case "ALLOWED_VALUES": {
      const values = Array.isArray(config.values) ? (config.values as string[]) : [];
      const textValue = values.join("\n");
      return (
        <div>
          <textarea
            value={textValue}
            onChange={(e) => {
              const lines = e.target.value.split("\n").filter((l) => l.trim() !== "");
              onChange({ values: lines });
            }}
            rows={4}
            className="w-full border border-neutral-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Jedna wartość na linię"
          />
          <p className="text-neutral-500 text-xs mt-0.5">
            Jedna wartość w każdym wierszu. Puste wiersze są pomijane.
          </p>
        </div>
      );
    }

    case "NUMERIC_RANGE": {
      const min = typeof config.min === "number" ? config.min : 0;
      const max = typeof config.max === "number" ? config.max : 100;
      return (
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="block text-xs text-neutral-500 mb-0.5">Min</label>
            <input
              type="number"
              step={1}
              value={min}
              onChange={(e) => onChange({ ...config, min: parseInt(e.target.value, 10) || 0 })}
              className="w-full border border-neutral-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-neutral-500 mb-0.5">Max</label>
            <input
              type="number"
              step={1}
              value={max}
              onChange={(e) => onChange({ ...config, max: parseInt(e.target.value, 10) || 0 })}
              className="w-full border border-neutral-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      );
    }

    case "DECIMAL_RANGE": {
      const min = typeof config.min === "number" ? config.min : 0;
      const max = typeof config.max === "number" ? config.max : 100;
      return (
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="block text-xs text-neutral-500 mb-0.5">Min</label>
            <input
              type="number"
              step={0.01}
              value={min}
              onChange={(e) => onChange({ ...config, min: parseFloat(e.target.value) || 0 })}
              className="w-full border border-neutral-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-neutral-500 mb-0.5">Max</label>
            <input
              type="number"
              step={0.01}
              value={max}
              onChange={(e) => onChange({ ...config, max: parseFloat(e.target.value) || 0 })}
              className="w-full border border-neutral-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      );
    }

    case "DATE_RANGE": {
      const min = typeof config.min === "string" ? config.min : "";
      const max = typeof config.max === "string" ? config.max : "";
      return (
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="block text-xs text-neutral-500 mb-0.5">Od (opcjonalnie)</label>
            <input
              type="date"
              value={min}
              onChange={(e) => {
                const newConfig = { ...config };
                if (e.target.value) {
                  newConfig.min = e.target.value;
                } else {
                  delete newConfig.min;
                }
                onChange(newConfig);
              }}
              className="w-full border border-neutral-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-neutral-500 mb-0.5">Do (opcjonalnie)</label>
            <input
              type="date"
              value={max}
              onChange={(e) => {
                const newConfig = { ...config };
                if (e.target.value) {
                  newConfig.max = e.target.value;
                } else {
                  delete newConfig.max;
                }
                onChange(newConfig);
              }}
              className="w-full border border-neutral-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      );
    }

    case "REGEX":
      return <RegexConfigForm config={config} onChange={onChange} />;

    case "BOOLEAN": {
      const defaultValue = typeof config.default === "boolean" ? config.default : undefined;
      return (
        <div>
          <label className="flex items-center gap-2 text-xs text-neutral-700">
            <input
              type="checkbox"
              checked={defaultValue === true}
              onChange={(e) => {
                if (e.target.checked) {
                  onChange({ ...config, default: true });
                } else {
                  const newConfig = { ...config };
                  delete newConfig.default;
                  onChange(newConfig);
                }
              }}
              className="h-3.5 w-3.5 text-primary-600 border-neutral-300 rounded"
            />
            Domyślna wartość (opcjonalnie)
          </label>
        </div>
      );
    }

    case "UNCONSTRAINED":
    default:
      return (
        <p className="text-xs text-neutral-400 italic py-1">(brak konfiguracji)</p>
      );
  }
};
